import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

const FancyLoader = ({
  message = 'Loading dashboard data...',
  size = 'large',
  fullScreen = false,
}) => {
  const sizeMap = {
    small: 40,
    medium: 60,
    large: 80,
  };

  const loaderSize = sizeMap[size] || sizeMap.large;

  return (
    <Box
      className="fancy-loader-container"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: fullScreen ? '100vh' : '60vh',
        background: fullScreen
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : 'transparent',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: fullScreen ? '0' : '12px',
        margin: fullScreen ? '0' : '20px',
        // boxShadow: fullScreen ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.1)',
        padding: fullScreen ? '0' : '40px',
        isolation: 'isolate', // Additional CSS isolation
      }}
    >
      {/* Animated background particles */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '20px',
            height: '20px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            animation: 'fancyLoaderFloat 6s ease-in-out infinite',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '60%',
            right: '15%',
            width: '15px',
            height: '15px',
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '50%',
            animation: 'fancyLoaderFloat 8s ease-in-out infinite reverse',
          },
        }}
      />

      {/* Main loader container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          zIndex: 1,
          position: 'relative',
        }}
      >
        {/* Animated circular progress with custom styling */}
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            '& .MuiCircularProgress-root': {
              color: '#fff',
              filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.3))',
            },
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        >
          <CircularProgress
            size={loaderSize}
            thickness={4}
            sx={{
              '& .MuiCircularProgress-circle': {
                stroke: 'url(#gradient)',
              },
            }}
          />

          {/* Gradient definition for the progress circle */}
          <svg width={0} height={0}>
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="50%" stopColor="#FF6B6B" />
                <stop offset="100%" stopColor="#4ECDC4" />
              </linearGradient>
            </defs>
          </svg>

          {/* Pulsing center dot */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '12px',
              height: '12px',
              background: 'linear-gradient(45deg, #FFD700, #FF6B6B)',
              borderRadius: '50%',
              animation: 'fancyLoaderPulse 2s ease-in-out infinite',
              boxShadow: '0 0 20px rgba(255, 215, 0, 0.5)',
            }}
          />
        </Box>

        {/* Loading text with typing animation */}
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            fontWeight: 600,
            textAlign: 'center',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            animation: 'fancyLoaderFadeInUp 1s ease-out',
            '&::after': {
              content: '""',
              animation: 'fancyLoaderTyping 2s steps(20, end) infinite',
            },
          }}
        >
          {message}
        </Typography>

        {/* Animated dots */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            '& > div': {
              width: '8px',
              height: '8px',
              background: '#fff',
              borderRadius: '50%',
              animation: 'fancyLoaderBounce 1.4s ease-in-out infinite both',
            },
            '& > div:nth-of-type(1)': {
              animationDelay: '-0.32s',
            },
            '& > div:nth-of-type(2)': {
              animationDelay: '-0.16s',
            },
          }}
        >
          <Box />
          <Box />
          <Box />
        </Box>

        {/* Progress bar */}
        <Box
          sx={{
            width: '200px',
            height: '4px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '2px',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '-100%',
              width: '100%',
              height: '100%',
              background:
                'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
              animation: 'fancyLoaderShimmer 2s infinite',
            },
          }}
        />
      </Box>

      {/* Custom CSS Animations with unique names */}
      <style jsx>{`
        .fancy-loader-container {
          isolation: isolate;
        }

        /* Custom animation names to avoid conflicts */
        @keyframes fancyLoaderFloat {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes fancyLoaderPulse {
          0%,
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.8;
          }
        }

        @keyframes fancyLoaderFadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fancyLoaderBounce {
          0%,
          80%,
          100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }

        @keyframes fancyLoaderShimmer {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        @keyframes fancyLoaderTyping {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </Box>
  );
};

export default FancyLoader;
